import type { FC, ReactNode } from 'react';
import { useMemo } from 'react';
import { Box, Stack, Typography, useTheme } from '@mui/material';

import BarWithPill from '../Bar/BarWithPill';
import { nextSingleDigitNonZero } from '../ChartBackground/calculateBarChartValues';
import ChartBackground from '../ChartBackground/ChartBackground';
import type { PillProps } from '../Pill/Pill';

type Bar = {
  label: string;
  absoluteValue: number;
  relativeValue: number;
};

interface GroupedBarChartProps {
  bars: Bar[];
  comparisonBars?: Bar[];
  isRelative?: boolean;
  yAxisLabel?: string;
}

const barsAreAllZero = (bars?: Bar[]) =>
  !!bars && bars.length > 0 && !bars?.find((bar) => bar.absoluteValue > 0);

// Helper function to create pill content with consistent structure
const createPillContent = (
  relativeValue: number,
  absoluteValue: number,
  colorScheme: 'secondary' | 'comparison',
  colors: any,
): ReactNode => {
  const colorKey = colorScheme === 'secondary' ? 'secondary' : 'comparison';
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Typography
        variant="caption2"
        color={colors[colorKey][colorScheme === 'secondary' ? 800 : 700]}
        fontWeight={600}
      >
        {relativeValue.toLocaleString()}%
      </Typography>
      <Typography
        variant="caption2"
        color={colors[colorKey][colorScheme === 'secondary' ? 800 : 700]}
        bgcolor={colors[colorKey][colorScheme === 'secondary' ? 200 : 50]}
        borderRadius="4px"
        px="2px"
      >
        {absoluteValue.toLocaleString()}
      </Typography>
    </Box>
  );
};

// Helper function to create BarWithPill props
const getPropsForBarWithPill = (
  barData: Bar,
  isComparison: boolean,
  barsAreAllZero: boolean,
  maxValue: number,
  isRelative: boolean | undefined,
  colors: any,
) => {
  const colorScheme = isComparison ? 'comparison' : 'secondary';
  const colorKey = isComparison ? 'comparison' : 'secondary';

  return {
    type: 'vertical' as const,
    relativeSize: barsAreAllZero
      ? maxValue
      : isRelative
        ? barData.relativeValue
        : (barData.absoluteValue * 100) / maxValue,
    borderPosition: 'Top' as const,
    color: colors[colorKey][isComparison ? 200 : 500],
    borderColor: colors[colorKey][isComparison ? 500 : 400],
    noDataAvailable: barsAreAllZero,
    ...(isComparison && { dontOverwritePillBg: true, maxWidth: 64 }),
    pill: barsAreAllZero
      ? undefined
      : ({
          label: createPillContent(
            barData.relativeValue,
            barData.absoluteValue,
            colorScheme,
            colors,
          ),
          color: isComparison ? 'comparisonSecondary' : 'secondary',
          squared: true,
        } as PillProps),
    withGradient: true,
  };
};

const VerticalBarChart: FC<GroupedBarChartProps> = ({
  bars,
  comparisonBars,
  yAxisLabel,
  isRelative,
}) => {
  const { colors } = useTheme();

  const comparisonBarsAreAllZero: boolean = useMemo(
    () => barsAreAllZero(comparisonBars),
    [comparisonBars],
  );
  const regularBarsAreAllZero = useMemo(() => barsAreAllZero(bars), [bars]);

  const maxValue = isRelative
    ? 100
    : nextSingleDigitNonZero(
        [...bars, ...(comparisonBars ?? [])].reduce(
          (max, bar) => (bar.absoluteValue > max ? bar.absoluteValue : max),
          9,
        ),
      );

  return (
    <Box height="100%" padding={4} paddingTop={6} width="100%">
      <ChartBackground
        maxValue={maxValue}
        numGridLines={4}
        labelsEachNthRow={1}
        showDecimals={0}
        yAxisLabel={yAxisLabel}
        isRelative={isRelative}
      >
        <Box sx={{ display: 'flex', height: '100%', gap: 3 }}>
          {bars.map((bar) => (
            <Box
              key={bar.label}
              width="100%"
              display="flex"
              flexDirection="column"
              gap={1}
            >
              <Stack direction="row" gap={1} flexGrow={1}>
                <BarWithPill
                  {...getPropsForBarWithPill(
                    bar,
                    false,
                    regularBarsAreAllZero,
                    maxValue,
                    isRelative,
                    colors,
                  )}
                />
                {comparisonBars &&
                  comparisonBars.find(
                    (comparisonBar) => comparisonBar.label === bar.label,
                  ) && (
                    <BarWithPill
                      {...(getPropsForBarWithPill(
                        comparisonBars.find(
                          (comparisonBar) => comparisonBar.label === bar.label,
                          // we can assume this will always return a value since we check for its existence above
                        ) as Bar,
                        true,
                        comparisonBarsAreAllZero,
                        maxValue,
                        isRelative,
                        colors,
                      ) || {})}
                    />
                  )}
              </Stack>
              <Typography variant="body3" height={'2.5rem'}>
                {bar.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </ChartBackground>
    </Box>
  );
};

export default VerticalBarChart;
